import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  TextField,
  useTheme,
  FormControl,
  useMediaQuery,
  Skeleton,
  Card,
} from '@mui/material';
import { Send as SendIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Controller } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import useUploadTramitesSAT from '../hooks/useUploadTramitesSAT';
import FileUploadBox from './FileUploadBox';
import SolicitudInfoCard, { InfoItem } from './SolicitudInfoCard';
import { UPLOAD_CONFIG } from '../constants/fileStatus';
import { ButtonBackMobile } from '../../Breadcrumb/ButtonBackMobile';

const filledStyle = {
  variant: 'filled',
  // InputProps:{ disableUnderline: true },
  sx: {
    '& .MuiFilledInput-root': {
      backgroundColor: '#fff',
      borderTopLeftRadius: '8px',
      borderTopRightRadius: '8px',
      boxShadow: '0px 0px 1px rgba(0, 0, 0, 0.08)',
      '&:hover': {
        backgroundColor: '#e0e0e0',
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff',
      },
    },
  },
};

/**
 * Componente principal para carga de trámites SAT
 * Permite cargar documentación requerida para un trámite específico
 * Utiliza token de URL para obtener datos del trámite
 *
 * @param {Object} props - Props del componente
 * @param {Object} props.message - Estado de mensajes { open, message, severity }
 * @param {Function} props.setMessage - Función para actualizar mensajes
 */
export const UploadDocumentacion = ({ autorizacion, message, setMessage }) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));

  const history = useHistory();
  const {
    // Estado
    tramiteData,
    isLoading,
    error,
    documentacionFiles,
    isSubmitting,

    // React Hook Form
    control,
    handleSubmit,
    errors,

    // Métodos
    setFiles,
    onSubmit,

    canSubmit,
    isSuccess,
  } = useUploadTramitesSAT({ autorizacion, setMessage });

  const { afiliado, numerodocumento, idSolicitudCoberturaFormateado, tipoCobertura, estado, observacion, documentacionTipoCobertura } =
    tramiteData || {};

  if (isSuccess) {
    history.push('/autorizaciones');
    return null;
  }

  const handleFilesChange = (idDocumentacion, newFiles) => {
    setFiles(idDocumentacion, newFiles);
  };

  return (
    <>
      <Box
        sx={{
          height: '32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <ButtonBackMobile />
      </Box>
      {!isMobileScreen && (
        <Container disableGutters maxWidth='md'>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Header */}
            {/* <Box sx={{ mb: 4 }}> */}
            <Typography gutterBottom variant='h5' component='h2' fontWeight={'bold'}>
              Completar Solicitud
            </Typography>
            {/* </Box> */}

            {/* Formulario */}
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Stack spacing={3}>
                <SolicitudInfoCard>
                  <InfoItem
                    label='Trámite Nro.'
                    value={
                      <Typography
                        component='span'
                        fontWeight={theme.typography.fontWeightBold}
                        color={theme.palette.primary.main}
                      >{`#${idSolicitudCoberturaFormateado}`}</Typography>
                    }
                  />
                  <InfoItem label='Afiliado' value={`(${numerodocumento}) ${afiliado}`} />
                  <InfoItem label='Tipo de Trámite' value={tipoCobertura} />
                </SolicitudInfoCard>

                {/* Mensaje del auditor */}
                {observacion && (
                  <SolicitudInfoCard severity='warning'>
                    <InfoItem label='Mensaje del auditor' value={observacion} severity='warning' />
                  </SolicitudInfoCard>
                )}

                {/* Documentación a Adjuntar */}
                <Box>
                  <Typography variant='h6' fontWeight='600' gutterBottom>
                    Documentación a Adjuntar
                  </Typography>

                  {/* Mensaje de información */}
                  <Alert severity='info' sx={{ borderRadius: 2, mb: 3 }}>
                    <Typography variant='body2' gutterBottom>
                      <strong>Adjuntá la documentación</strong> para completar correctamente tu trámite.
                    </Typography>
                    <Typography
                      variant='body2'
                      gutterBottom
                      color='text.secondary'
                      fontSize={'0.775rem'}
                      fontWeight={'600'}
                    >
                      Formatos permitidos PDF, JPG, JPEG · Hasta {UPLOAD_CONFIG.MAX_FILES_PER_DOC}{' '}
                      archivos · Máx. {UPLOAD_CONFIG.MAX_FILE_SIZE_MB}MB por archivo.
                    </Typography>
                  </Alert>

                  <Stack spacing={3}>
                    {isLoading && !documentacionTipoCobertura && (
                      <>
                        {[1, 2].map((i) => (
                          <Card
                            key={i}
                            elevation={0}
                            variant='outlined'
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              boxShadow: 'none',
                              p: 2,
                            }}
                          >
                            <Skeleton variant='rectangular' height={28} animation='wave' sx={{ mb: 2, width: '50%' }} />
                            <Skeleton variant='rectangular' height={80} animation='wave' />
                          </Card>
                        ))}
                      </>
                    )}
                    {documentacionTipoCobertura
                      ?.filter((doc) => doc.activo)
                      .map((doc) => (
                        <FileUploadBox
                          key={doc.idDocumentacion}
                          label={doc.documentacion}
                          required={doc.requerido}
                          files={documentacionFiles[doc.idDocumentacion] || []}
                          onFilesChange={(newFiles) => handleFilesChange(doc.idDocumentacion, newFiles)}
                          maxFiles={UPLOAD_CONFIG.MAX_FILES_PER_DOC}
                          maxSizeMB={UPLOAD_CONFIG.MAX_FILE_SIZE_MB}
                          setMessage={setMessage}
                        />
                      ))}
                  </Stack>
                </Box>

                {/* Observaciones */}
                <Box>
                  <Typography variant='h6' fontWeight='600' gutterBottom>
                    Observaciones
                  </Typography>
                  <FormControl fullWidth={true} error={!!errors.observacion} variant='standard'>
                    <Controller
                      name='observacion'
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label='Observaciones adicionales (opcional)'
                          fullWidth
                          multiline
                          rows={3}
                          placeholder='Ingresá tus observaciones (opcional)...'
                          variant='outlined'
                          error={!!errors.observacion}
                          helperText={errors.observacion?.message}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          {...filledStyle}
                        />
                      )}
                    />
                  </FormControl>
                </Box>

                {/* Botones */}
                <Box sx={{
                  pt: 2,
                  width: '100%',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'flex-end',
                  gap: 2,
                }}>
                  <Button
                    variant='contained'
                    color='neutral'
                    disabled={isSubmitting}
                    startIcon={<ArrowBackIcon />}
                    onClick={() => history.push('/autorizaciones')}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type='submit'
                    variant='contained'
                    disabled={isSubmitting || !canSubmit().can}
                    startIcon={
                      isSubmitting ? <CircularProgress size={20} color='inherit' /> : <SendIcon />
                    }
                    sx={{
                      boxShadow: 0,
                      '&:hover': {
                        boxShadow: 2,
                      },
                    }}
                  >
                    {isSubmitting ? 'Enviando trámite...' : 'Enviar Trámite'}
                  </Button>

                </Box>
                {/* Mensaje de ayuda si no se puede enviar */}
                {!canSubmit().can && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ m: 2, display: 'block', textAlign: 'center' }}
                  >
                    {canSubmit().reason}
                  </Typography>
                )}
              </Stack>
            </form>
          </Paper>

          {/* Footer informativo */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant='caption' color='text.secondary'>
              ¿Tiene problemas con la carga? Verifique que los archivos cumplan con los requisitos de
              formato y tamaño.
            </Typography>
          </Box>
        </Container>
      )}
      {isMobileScreen && (
        <Container disableGutters maxWidth='md'>
          {/* Header */}
          {/* <Box sx={{ mb: 4 }}> */}
          <Typography gutterBottom variant='h5' component='h2' fontWeight={'bold'}>
            Completar Solicitud
          </Typography>
          {/* </Box> */}

          {/* Formulario */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Stack spacing={3}>
              <SolicitudInfoCard>
                <InfoItem
                  label='Trámite Nro.'
                  value={
                    <Typography
                      component='span'
                      fontWeight={theme.typography.fontWeightBold}
                      color={theme.palette.primary.main}
                    >{`#${idSolicitudCoberturaFormateado}`}</Typography>
                  }
                />
                <InfoItem label='Afiliado' value={`(${numerodocumento}) ${afiliado}`} />
                <InfoItem label='Tipo de Trámite' value={tipoCobertura} />
              </SolicitudInfoCard>

              {/* Mensaje del auditor */}
              {observacion && (
                <SolicitudInfoCard severity='warning'>
                  <InfoItem label='Mensaje del auditor' value={observacion} severity='warning' />
                </SolicitudInfoCard>
              )}

              {/* Documentación a Adjuntar */}
              <Box>
                <Typography variant='h6' fontWeight='600' gutterBottom>
                  Documentación a Adjuntar
                </Typography>

                {/* Mensaje de información */}
                <Alert severity='info' sx={{ borderRadius: 2, mb: 3 }}>
                  <Typography variant='body2' gutterBottom>
                    <strong>Adjuntá la documentación</strong> para completar correctamente tu trámite.
                  </Typography>
                  <Typography
                    variant='body2'
                    gutterBottom
                    color='text.secondary'
                    fontSize={'0.775rem'}
                    fontWeight={'600'}
                  >
                    Formatos permitidos PDF, JPG, JPEG · Hasta {UPLOAD_CONFIG.MAX_FILES_PER_DOC}{' '}
                    archivos · Máx. {UPLOAD_CONFIG.MAX_FILE_SIZE_MB}MB por archivo.
                  </Typography>
                </Alert>

                <Stack spacing={3}>
                  {isLoading && !documentacionTipoCobertura && (
                    <>
                      {[1, 2].map((i) => (
                        <Card
                          key={i}
                          elevation={0}
                          variant='outlined'
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            boxShadow: 'none',
                            p: 2,
                          }}
                        >
                          <Skeleton variant='rectangular' height={28} animation='wave' sx={{mb: 2, width: '50%'}} />
                          <Skeleton variant='rectangular' height={53} animation='wave' />
                        </Card>
                      ))}
                    </>
                  )}
                  {documentacionTipoCobertura
                    ?.filter((doc) => doc.activo)
                    .map((doc) => (
                      <FileUploadBox
                        key={doc.idDocumentacion}
                        label={doc.documentacion}
                        required={doc.requerido}
                        files={documentacionFiles[doc.idDocumentacion] || []}
                        onFilesChange={(newFiles) => handleFilesChange(doc.idDocumentacion, newFiles)}
                        maxFiles={UPLOAD_CONFIG.MAX_FILES_PER_DOC}
                        maxSizeMB={UPLOAD_CONFIG.MAX_FILE_SIZE_MB}
                        setMessage={setMessage}
                        isLoading={isLoading}
                      />
                    ))}
                </Stack>
              </Box>

              {/* Observaciones */}
              <Box>
                <Typography variant='h6' fontWeight='600' gutterBottom>
                  Observaciones
                </Typography>
                <FormControl fullWidth={true} error={!!errors.observacion} variant='standard'>
                  <Controller
                    name='observacion'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label='Observaciones adicionales (opcional)'
                        fullWidth
                        multiline
                        rows={3}
                        placeholder='Ingresá tus observaciones (opcional)...'
                        variant='outlined'
                        error={!!errors.observacion}
                        helperText={errors.observacion?.message}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        {...filledStyle}
                      />
                    )}
                  />
                </FormControl>
              </Box>

              {/* Botones */}
              <Box sx={{
                pt: 2,
                width: '100%',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'flex-end',
                gap: 2,
              }}>
                <Button
                  variant='contained'
                  size='large'
                  color='neutral'
                  disabled={isSubmitting}
                  startIcon={<ArrowBackIcon />}
                  onClick={() => history.push('/autorizaciones')}
                  sx={{
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  size='large'
                  fullWidth
                  disabled={isSubmitting || !canSubmit().can}
                  startIcon={
                    isSubmitting ? <CircularProgress size={20} color='inherit' /> : <SendIcon />
                  }
                  sx={{
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                    },
                  }}
                >
                  {isSubmitting ? 'Enviando trámite...' : 'Enviar Trámite'}
                </Button>

                {/* Mensaje de ayuda si no se puede enviar */}
                {!canSubmit().can && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ mt: 1, display: 'block', textAlign: 'center' }}
                  >
                    {canSubmit().reason}
                  </Typography>
                )}
              </Box>
            </Stack>
          </form>
          {/* </Paper> */}

          {/* Footer informativo */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant='caption' color='text.secondary'>
              ¿Tiene problemas con la carga? Verifique que los archivos cumplan con los requisitos de
              formato y tamaño.
            </Typography>
          </Box>
        </Container>
      )}
    </>
  );
};
